import {useMemo, useState} from "react";
import {FontBody2} from "../fonts/font-body2/font-body2";
import {FONT_COLOR} from "../fonts/font-props";
import RoundLoader from "../loaders/round-loader/round-loader";
import {DefaultModalLayout} from "../modal/layouts/default-modal-layout";
import {Tooltip, TOOLTIP_VARIANTS} from "../tooltip/tooltip";
import MediaErrorLoading from './media-error-loading.svg';
import MediaNotAvailable from './media-not-available.svg';
import VideoPlayButton from './video-play-button.svg';
import './media-with-placeholder.scss';
import { BUTTON_VARIANTS } from "../inputs/button-variants";
import { ButtonLink } from "../inputs/button-link/button-link";
import useDashboardTranslation from "../../dashboard-translation/hooks/dashboard-translation-hook";
import FontBody3 from "../fonts/font-body3/font-body3";

export enum MEDIA_VARIANTS {
    XS = 'xs',
    SMALL = 'small'
}
export interface IMediaWithPlaceholderProps {
    src?: string;
    alt?: string;
    /* if we know the media does not exist - i.e. failed upload*/
    mediaNotAvailable?: boolean;
    /* add src to enable enlarging media*/
    enlargeSrc?: string;
    variant: MEDIA_VARIANTS;
}

const MediaWithPlaceholder: React.FC<IMediaWithPlaceholderProps> = ({src, alt, mediaNotAvailable,  enlargeSrc, variant}) => {
    const { getTranslation } = useDashboardTranslation();
    const generalScope = 'dashboard-general';
    
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [enlargeOpen, setEnlargeOpen] = useState(false);
    
    const isVideo = useMemo(()=>{
        /* Taken from portal file upload dialog */
        const videoFileExtension = ['heif', 'm4v', 'mp4', 'mpg', 'mpeg', 'mov'];
        const ext = src?.toLowerCase()?.split('.').pop() ?? '';
        if(videoFileExtension.includes(ext)){
            return true;
        }
        else {
            return false;
        }
    }, [src]);
    
    const hasEnlarge = isVideo || enlargeSrc;
    
    if (mediaNotAvailable) {
        return (
            <Tooltip  variant={TOOLTIP_VARIANTS.TEXT} content={<FontBody2 color={FONT_COLOR.PRIMARY3_50}>Media Not Available</FontBody2>}>
                <div className={"media-with-placeholder variant-" + variant + " media-not-available"}>
                    <img src={MediaNotAvailable} alt="Not available"/>
                </div>
            </Tooltip>
        )
    }
    const onError = () => {
        setHasError(true);
        setIsLoading(false);
    }
    
    const onLoad = () => {
        setHasError(false);
        setIsLoading(false);
    }
    
    const onClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.stopPropagation();
        if (!hasEnlarge || hasError) {
            return;
        }
        setEnlargeOpen(true);
    }
    
    let MediaElement: JSX.Element, MediaElementEnlarged: JSX.Element = <></>;
    if(isVideo){
        MediaElement = (<>
            <video src={src} autoPlay={false} controls={false} onError={onError} onLoadedData={onLoad}/>
            <div className={'video-play-button'}>
                {<img src={VideoPlayButton} alt=""/>}
            </div>
        </>);
    }
    else {
        MediaElement = <img className={'media-image'} src={src} alt={alt ?? ''} onError={onError} onLoad={onLoad}/>;
    }
    
    const Media = (
        <div className={"media-with-placeholder variant-" + variant +  (hasError ? ' media-error-loading' : '') + (hasEnlarge ? ' media-has-enlarge' : '') + (isLoading ? ' media-loading' : '') + (isVideo ? ' media-video' : ' media-image')} onClick={onClick}>
            { hasError && <img className={'media-error'} src={MediaErrorLoading} alt={alt ?? ''}/> }
            { isLoading && <RoundLoader />}
            {MediaElement}
        </div>
    );
    if(hasError){
        return (
            <Tooltip variant={TOOLTIP_VARIANTS.TEXT} content={
                <>
	                <FontBody2 color={FONT_COLOR.PRIMARY3_50}>Error Loading Media</FontBody2>
	                {src && (
		                <FontBody3 color={FONT_COLOR.PRIMARY3_50}>
			                <a href={src} download target="_blank" rel="noopener noreferrer">Download File</a>
		                </FontBody3>
	                )}
                </>
            }>
                {Media}
            </Tooltip>
        );
    }
    
    if(enlargeOpen){
        if(isVideo){
            MediaElementEnlarged = <video src={src} controls={true} onError={onError} onLoad={onLoad} autoPlay={true}/>;
        }
        else {
            MediaElementEnlarged = <img className={'media-image'} src={enlargeSrc ?? src} alt={alt ?? ''} style={{width: '100%', height: '100%'}}/>;
        }
    }
    
    return (
        <>
            {Media}
            { enlargeOpen &&
                <div onClick={e => e.stopPropagation()}>
                    <DefaultModalLayout 
                        isCloseDisabled={false} 
                        onCloseClicked={() => setEnlargeOpen(false)}
                        headerContent={
                            <div className="media-with-placeholder-modal-header-content">
                                <ButtonLink href={enlargeSrc ?? src} target="_blank" variant={BUTTON_VARIANTS.Text} text={getTranslation('VIEW_IN_FULL_SIZE', generalScope)} />
                            </div>
                        }
                    >
                        <div className={'media-enlarge-layout' + (isVideo ? ' media-video' : ' media-image')}>
                            {MediaElementEnlarged}
                        </div>
                    </DefaultModalLayout>
                </div>
            }
        </>
    );
}

export default MediaWithPlaceholder;